import { createSignedUploadUrlMutationDocument } from "@/domain/_shared/file/hooks/api/signed-url.gql";
import { extractObjectKeyFromPermaUrl } from "@/domain/_shared/file/lib/extract-object-key";
import { publishUploadedAsset } from "@/domain/_shared/file/lib/publish-uploaded-asset";
import { PublishedAssetType } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";

const BYTES_PER_MEGABYTE = 1024 * 1024;
const HTTP_FORBIDDEN = 403;
const HTTP_PAYLOAD_TOO_LARGE = 413;

interface AssetUploadOptions {
  maxFileSize: number;
  assetType: PublishedAssetType;
  allowedMimeType?: string;
}

type UploadHandler = (
  file: File,
  onProgress?: (event: { progress: number }) => void,
  abortSignal?: AbortSignal,
) => Promise<string>;

// Signed URL → PUT → publish is one pipeline; images and PDFs differ only in
// their size cap, asset type and whether a MIME type is enforced. Parametrizing
// it keeps the two from drifting apart, in particular on the error handling and
// the owner-bound object key the publish mutation requires.
export function createAssetUploadHandler({
  maxFileSize,
  assetType,
  allowedMimeType,
}: AssetUploadOptions): ({ workspaceId }?: { workspaceId?: string }) => UploadHandler {
  return ({ workspaceId }: { workspaceId?: string } = {}) =>
    async (file, onProgress, abortSignal) =>
      await uploadAsset({ file, onProgress, abortSignal, workspaceId, maxFileSize, assetType, allowedMimeType });
}

async function uploadAsset({
  file,
  onProgress,
  abortSignal,
  workspaceId,
  maxFileSize,
  assetType,
  allowedMimeType,
}: AssetUploadOptions & {
  file: File;
  onProgress?: (event: { progress: number }) => void;
  abortSignal?: AbortSignal;
  workspaceId?: string;
}): Promise<string> {
  if (file.size > maxFileSize) {
    throw new Error(
      `The file "${file.name}" (${(file.size / BYTES_PER_MEGABYTE).toFixed(1)}MB) exceeds the maximum allowed size of ${maxFileSize / BYTES_PER_MEGABYTE}MB. Please reduce the file size and try again.`,
    );
  }

  if (allowedMimeType && file.type !== allowedMimeType) {
    throw new Error(
      `The file "${file.name}" has an unsupported type (${file.type || "unknown"}). Only ${allowedMimeType} files are allowed.`,
    );
  }

  const { signedUrl, permaUrl } = await requestSignedUrl(file, workspaceId);
  const objectKey = readOwnerBoundObjectKey({ permaUrl, fileName: file.name });

  onProgress?.({ progress: readFakeProgress({ from: 0.21, to: 0.37 }) });

  await putObject({ file, signedUrl, abortSignal });

  onProgress?.({ progress: readFakeProgress({ from: 0.57, to: 0.91 }) });

  await publishAsset({ fileName: file.name, objectKey, assetType, workspaceId });

  onProgress?.({ progress: 100 });

  return permaUrl;
}

async function requestSignedUrl(file: File, workspaceId?: string): Promise<{ signedUrl: string; permaUrl: string }> {
  let data;
  try {
    const client = createOperationClient("createSignedUploadUrl");
    data = await client.request(createSignedUploadUrlMutationDocument, {
      input: {
        fileExtension: file.name.split(".").pop() || "",
        type: file.type,
        workspaceId,
      },
    });
  } catch (error) {
    throw new Error(
      `Could not prepare upload for "${file.name}": ${error instanceof Error ? error.message : "The server may be temporarily unavailable. Please try again later."}`,
      { cause: error },
    );
  }

  if (!data?.createSignedUploadUrl?.signedUrl) {
    throw new Error(
      `Could not prepare upload for "${file.name}". The server did not return a valid upload URL. Please try again later.`,
    );
  }

  return data.createSignedUploadUrl;
}

// The publish mutation rejects bare basenames, so the full object key — including
// the `${userId}/` prefix the server mints for ownership binding — has to come
// back out of the permaUrl. Failing here beats publishing an unbindable key.
function readOwnerBoundObjectKey({ permaUrl, fileName }: { permaUrl: string; fileName: string }): string {
  const objectKey = extractObjectKeyFromPermaUrl(permaUrl);
  if (!objectKey || !objectKey.includes("/")) {
    throw new Error(
      `Could not derive an owner-bound object key for "${fileName}". The server returned a malformed upload URL.`,
    );
  }
  return objectKey;
}

async function putObject({
  file,
  signedUrl,
  abortSignal,
}: {
  file: File;
  signedUrl: string;
  abortSignal?: AbortSignal;
}): Promise<void> {
  let response;
  try {
    response = await fetch(signedUrl, {
      method: "PUT",
      body: file,
      signal: abortSignal,
      headers: { "Content-Type": file.type },
    });
  } catch (error) {
    if (abortSignal?.aborted) {
      throw new Error("Upload was cancelled.");
    }
    throw new Error(
      `Network error while uploading "${file.name}". Please check your internet connection and try again.`,
      { cause: error },
    );
  }

  if (response.ok) {
    return;
  }

  if (response.status === HTTP_FORBIDDEN) {
    throw new Error(`Upload of "${file.name}" was denied (HTTP 403). The upload URL may have expired. Please try again.`);
  }
  if (response.status === HTTP_PAYLOAD_TOO_LARGE) {
    throw new Error(
      `The file "${file.name}" is too large for the server (HTTP 413). Please reduce the file size and try again.`,
    );
  }
  throw new Error(`Failed to upload "${file.name}" to storage (HTTP ${response.status}). Please try again later.`);
}

async function publishAsset({
  fileName,
  objectKey,
  assetType,
  workspaceId,
}: {
  fileName: string;
  objectKey: string;
  assetType: PublishedAssetType;
  workspaceId?: string;
}): Promise<void> {
  let publicUrl;
  try {
    publicUrl = await publishUploadedAsset({ fileName: objectKey, assetType, workspaceId });
  } catch (error) {
    throw new Error(
      `The file "${fileName}" was uploaded but finalization failed: ${error instanceof Error ? error.message : "unknown error"}`,
      { cause: error },
    );
  }

  if (!publicUrl) {
    throw new Error(
      `The file "${fileName}" was uploaded but could not be made accessible. Please try again or contact support.`,
    );
  }
}

// The editor's progress bar has no real signal to follow: the PUT is a single
// opaque fetch. A jittered value keeps the bar from looking frozen.
function readFakeProgress({ from, to }: { from: number; to: number }): number {
  const PERCENT_FACTOR = 100;
  return Math.round((Math.random() * (to - from) + from) * PERCENT_FACTOR);
}
